// «Клиент 360°»: то, что нужно карточке клиента и не является разметкой.
//
// Своих формул по деньгам здесь нет и быть не может — правило файла pages/Client360.tsx
// действует и тут. Всё, что считается, считают модули ядра; здесь только разбор ОДНОГО
// значения — ИНН из карточки компании, — и он нужен ровно затем, чтобы спросить у ядра
// банковскую выписку по этому контрагенту.
//
// ── Почему ИНН вообще приходится разбирать ──
// Реквизитов (crm.requisite) в зеркале нет, и ИНН в Bitrix живёт в обычном строковом
// UF-поле, у которого даже названия нет: `bx_fields.title` совпадает с кодом, а
// человеческая подпись «123ИНН321» лежит в сыром описании поля и до `/fields` не доезжает.
// Поле свободного ввода, и туда пишут не только цифры. Замер по боевому зеркалу
// 28.07.2026 (`SELECT` по `companies`): заполнено у 4 450 карточек из 10 683, из них
// похоже на ИНН 4 071, а 379 — текст, и почти весь этот текст осмысленный:
// «Иностранный агент» стоит в 359 карточках (тот же замер, 28.07.2026). Это не мусор,
// это ответ «российского ИНН у клиента нет».
//
// Отсюда три исхода вместо двух, и экран обязан различать их словами:
//   • пусто            — поле не заполнили;
//   • похоже на ИНН    — можно искать платежи в банке;
//   • заполнено текстом — искать нечем, но человеку есть что прочитать.
// Свести второе и третье в «ИНН не указан» значило бы стереть содержательный ответ.

/**
 * Код поля ИНН в карточке компании Bitrix.
 *
 * Тот же код, по которому проверку `company-inn-dirty` («ИНН с пробелами по краям») считает
 * `dq_api.py:1722`. Один и тот же факт в двух местах должен читаться из одного поля: заведут
 * в Bitrix второе поле под ИНН — менять придётся оба места, и лучше, чтобы это было видно.
 */
export const ИНН_КОМПАНИИ = 'UF_CRM_1677500316'

/**
 * Как поле называется у людей. Своей подписи `/fields` не отдаёт (в `bx_fields.title` лежит
 * сам код), а в Bitrix на форме написано «123ИНН321» — цифры вокруг слова там с самого
 * заведения поля. Показывать их человеку незачем, но и выдумывать «ИНН» как официальное имя
 * поля нельзя: в отчёте о расхождении надо будет назвать поле так, как его видно в Bitrix.
 */
export const ИНН_ПОДПИСЬ = 'ИНН'
export const ИНН_ПОДПИСЬ_В_BITRIX = '123ИНН321'

export type ВидИНН = 'нет' | 'инн' | 'текст'

export interface ЧтоСИНН {
  вид: ВидИНН
  /** Значение как есть, без пробелов по краям. Пустая строка при `вид === 'нет'`. */
  значение: string
  /** Что сказать человеку рядом со значением. */
  подпись: string
}

/**
 * Разобрать поле ИНН карточки компании.
 *
 * Проверка «10 или 12 цифр» — это формат российского ИНН (юрлицо и физлицо/ИП), а не
 * контрольная сумма. Контрольную сумму здесь не считаем намеренно: сопоставление платежей
 * с контрагентами делает ядро (`pbh_api`, поиск по `btrim(cp_inn)`), и если оно сравнивает
 * строки, то и мы обязаны спрашивать его ровно тем же значением. Своя «правильность» ИНН
 * привела бы к тому, что экран отказался бы искать платежи, которые ядро находит.
 */
export function разобратьИНН(сырое: unknown): ЧтоСИНН {
  const v = String(сырое ?? '').trim()
  if (!v) {
    return { вид: 'нет', значение: '', подпись: 'в карточке Bitrix поле не заполнено' }
  }
  if (/^\d{10}$/.test(v) || /^\d{12}$/.test(v)) {
    return { вид: 'инн', значение: v, подпись: `${v.length} цифр — формат ИНН` }
  }
  return {
    вид: 'текст',
    значение: v,
    подпись: 'в поле не ИНН, а текст: искать платежи по нему нечем',
  }
}

/**
 * Итоги банковской выписки по контрагенту в том объёме, который показывает карточка.
 *
 * Повторяет `totals` из `pbh_api.выписка` — считает их ядро по ВСЕЙ выборке, а не по
 * странице. Это редкий на карточке клиента случай точной суммы, и он должен быть подписан
 * именно так: рядом с ним стоят суммы, посчитанные по загруженной странице, а не по всей
 * выборке, и без пометки человек решит, что и эта такая же.
 */
export interface ИтогиПоКонтрагенту {
  n: number
  in_n: number
  in_rub: number | null
  unlinked_n: number
  unlinked_rub: number | null
  first_day: string | null
  last_day: string | null
}

/** Есть ли что показывать в блоке банка. Ноль строк — это ответ, а не повод прятать блок. */
export const естьВыписка = (t: ИтогиПоКонтрагенту | undefined | null): boolean =>
  !!t && (t.n > 0 || t.unlinked_n > 0)
